import type { RootState } from '@/store';

import {
  modalidadeDeCobrancaArbitral,
  podeCriarLoteArbitral,
  resolverEstadoCriacao,
} from '../estadoCriacao';
import type { CategoriaComContrato, ContractCategory } from '../types';
import { franquiaEsgotada } from '../utils/franquiaCiclo';

export const selectActiveContractByCategory = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].data;

export const selectActiveContractLoading = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].loading;

export const selectActiveContractError = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].error;

// TODO(#713): o slice guarda o contrato por categoria, não por escritório, e o `.pending` não
// limpa `data` — logo após trocar de escritório este selector responde `true` com o contrato do
// anterior. Invalidar o cache na troca de workspace resolve; limpar no `.pending` não, porque
// reintroduz o flash a cada refetch.
export const selectActiveContractResolvido = (category: ContractCategory) => (state: RootState) => {
  const { data, error } = state.contracts[category];
  return data !== null || error !== null;
};

export const selectHasActiveContract = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].data?.hasActive ?? false;

export const selectIsAtTrialLimit = (category: ContractCategory) => (state: RootState) => {
  const data = state.contracts[category].data;
  return !!(data?.hasActive && data.status === 'TRIAL' && franquiaEsgotada(data.currentPeriod));
};

export const selectIsTrialBlocked = (category: ContractCategory) => (state: RootState) => {
  const data = state.contracts[category].data;
  return !!(data?.hasActive && data.isBlocked);
};

export const selectIsCreationBlockedByUsage =
  (category: ContractCategory) => (state: RootState) => {
    const data = state.contracts[category].data;
    return !!(
      data?.hasActive &&
      data.status !== 'TRIAL' &&
      data.paymentMode !== 'postpaid' &&
      franquiaEsgotada(data.currentPeriod)
    );
  };

export const selectEffectiveUnitPrice = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].unitPrice;

export const selectEffectiveUnitPriceLoading = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].unitPriceLoading;

export const selectCurrentPeriod = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].data?.currentPeriod ?? null;

export const selectContractStatus = (category: ContractCategory) => (state: RootState) =>
  state.contracts[category].data?.status ?? null;

/** Regra única de criação (lista, revisão, sidebar). Devolve primitivo: estável entre renders. */
export const selectEstadoCriacao = (categoria: CategoriaComContrato) => (state: RootState) =>
  resolverEstadoCriacao({
    categoria,
    contratoResolvido: selectActiveContractResolvido(categoria)(state),
    erroContrato: selectActiveContractError(categoria)(state) !== null,
    hasActive: selectHasActiveContract(categoria)(state),
    trialBloqueado: selectIsTrialBlocked(categoria)(state),
    limiteTeste: selectIsAtTrialLimit(categoria)(state),
    limiteCiclo: selectIsCreationBlockedByUsage(categoria)(state),
  });

export const selectModalidadeCobrancaArbitral = (state: RootState) =>
  modalidadeDeCobrancaArbitral({
    contratoResolvido: selectActiveContractResolvido('ARBITRAGEM')(state),
    erroContrato: selectActiveContractError('ARBITRAGEM')(state) !== null,
    hasActive: selectHasActiveContract('ARBITRAGEM')(state),
  });

export const selectPodeCriarLoteArbitral = (state: RootState) =>
  podeCriarLoteArbitral(selectModalidadeCobrancaArbitral(state));
